<template>
    <HeaderCardsLayout v-if="user" variant="No-Logo">
        <template #headline>
            <div class="flex items-center gap-4">
                {{ $t('edit-user') }}
                <LinkButton
                    v-if="user.isActive"
                    variant="Danger-Light"
                    size="Base"
                    :to="{ name: 'user-deactivate', params: { userId: user.id } }"
                >
                    <template #icon>
                        <BlockIcon class="size-5" />
                    </template>
                    <span class="font-normal">{{ $t('deactivate-preview') }}</span>
                </LinkButton>
            </div>
        </template>
        <template #breadcrumb>
            <Breadcrumb :headline="$t('breadcrumb')" :current-page="$t('edit-user')">
                <BreadcrumbLink :target="{ name: 'users' }">
                    <UsersIcon class="mr-1 shrink-0" />
                    {{ $t('users') }}
                </BreadcrumbLink>
                <BreadcrumbLink :target="{ name: 'user-details', params: { userId } }">
                    {{ user.firstName }} {{ user.lastName }}
                </BreadcrumbLink>
            </Breadcrumb>
        </template>
        <template #buttons>
            <Button
                variant="Danger-Light"
                size="Base"
                :disabled-explanation="discardButtonDisabledExplanation"
                @click="onDiscardChanges"
            >
                <template #icon>
                    <UndoIcon class="size-5" />
                </template>
                {{ $t('discard-changes') }}
            </Button>
            <Button
                variant="Neutral-Strong"
                size="Base"
                :disabled-explanation="saveButtonDisabledExplanation"
                :status="saveButtonStatus"
                @click="onSave"
            >
                <template #icon>
                    <AllDoneIcon class="size-5" />
                </template>
                {{ $t('save') }}
            </Button>
        </template>
        <template #body>
            <PaddedContent class="space-y-4">
                <AccentCard
                    v-if="user.passwordLogin && user.passwordLogin.isActive"
                    :variant="getAccentCardVariant('password')"
                >
                    <template #header="{ headlineId }">
                        <LoginMethodHeader
                            :headline-id="headlineId"
                            variant="Developer-Account"
                            :is-gray-scale="loginMethodMarkedForRemoval('password')"
                        >
                            <template #headline>{{ $t('email-password-login') }}</template>

                            <template #buttons>
                                <Button
                                    v-if="loginMethodMarkedForRemoval('password')"
                                    size="Extra-Small"
                                    variant="Neutral-Strong"
                                    @click="keepLoginMethod('password')"
                                >
                                    <template #icon>
                                        <UndoIcon class="size-4" />
                                    </template>
                                    {{ $t('keep-login-method') }}
                                </Button>
                                <Button
                                    v-else
                                    size="Extra-Small"
                                    variant="Danger-Strong"
                                    @click="removeLoginMethod('password')"
                                >
                                    <template #icon>
                                        <DeleteForeverIcon class="size-4" />
                                    </template>
                                    {{ $t('remove-login-method') }}
                                </Button>
                            </template>
                        </LoginMethodHeader>
                    </template>
                    <div class="flex items-start justify-between gap-4 px-2.75 py-3">
                        <CredentialsInfo :email="user.passwordLogin.email ?? ''" :show-password-hint="true" />
                    </div>
                    <template v-if="loginMethodMarkedForRemoval('password')" #footer>
                        <div class="flex flex-row items-center gap-1.5 p-1.5 pl-2">
                            <div class="size-5 flex items-center justify-center">
                                <BlockIcon class="size-4 shrink-0" />
                            </div>
                            <i18n path="user-sso-deactivate-message" tag="span" class="text-sm font-light">
                                <template #login="{ method }">
                                    <span class="font-semibold">{{ method }}</span>
                                </template>
                                <template #action="{ actionLabel }">
                                    <span class="font-semibold">{{ actionLabel }}</span>
                                </template>
                            </i18n>
                        </div>
                    </template>
                </AccentCard>
                <AccentCard
                    v-for="sso of user.ssoLogins.filter((sso) => sso.isActive)"
                    :key="sso.id"
                    :variant="getAccentCardVariant(sso.provider)"
                >
                    <template #header="{ headlineId }">
                        <LoginMethodHeader
                            :headline-id="headlineId"
                            :variant="sso.provider === SsoProvider.SsoKm ? 'Koelnmesse-SSO' : 'Generic-SSO'"
                            :is-gray-scale="loginMethodMarkedForRemoval(sso.provider)"
                        >
                            <template #headline>
                                {{ $t('provider-login-header', { provider: sso.provider }) }}
                            </template>

                            <template #buttons>
                                <Button
                                    v-if="loginMethodMarkedForRemoval(sso.provider)"
                                    size="Extra-Small"
                                    variant="Neutral-Strong"
                                    @click="keepLoginMethod(sso.provider)"
                                >
                                    <template #icon>
                                        <UndoIcon class="size-4" />
                                    </template>
                                    {{ $t('keep-login-method') }}
                                </Button>
                                <Button
                                    v-else
                                    size="Extra-Small"
                                    variant="Danger-Strong"
                                    @click="removeLoginMethod(sso.provider)"
                                >
                                    <template #icon>
                                        <DeleteForeverIcon class="size-4" />
                                    </template>
                                    {{ $t('remove-login-method') }}
                                </Button>
                            </template>
                        </LoginMethodHeader>
                    </template>
                    <div class="flex items-start justify-between gap-4 px-2.75 py-3">
                        <ContactInfo
                            v-if="sso.email"
                            :email="sso.email"
                            :variant="getContactInfoVariant(sso.provider)"
                        />
                        <div
                            v-else
                            class="p-2 pl-2.5 flex flex-row items-center gap-2 text-sm text-gray-700 font-light"
                        >
                            <WarningIcon class="size-4 text-gray-500 shrink-0" />
                            <span>{{ $t('data-loading-failed') }}</span>
                        </div>
                    </div>
                    <template v-if="loginMethodMarkedForRemoval(sso.provider)" #footer>
                        <div class="flex flex-row items-center gap-1.5 p-1.5 pl-2">
                            <div class="size-5 flex items-center justify-center">
                                <BlockIcon class="size-4 shrink-0" />
                            </div>
                            <i18n path="user-sso-deactivate-message" tag="span" class="text-sm font-light">
                                <template #login="{ method }">
                                    <span class="font-semibold">{{ method }}</span>
                                </template>
                                <template #action="{ actionLabel }">
                                    <span class="font-semibold">{{ actionLabel }}</span>
                                </template>
                            </i18n>
                        </div>
                    </template>
                </AccentCard>
            </PaddedContent>
            <PaddedContent class="bg-white">
                <h2 class="font-semibold text-gray-700 text-xl mb-2">
                    {{ $t('permissions') }}
                </h2>
                <RadioButtonGroup
                    :label="$t('access-permission')"
                    name="access-permission"
                    :is-required="true"
                    :options="accessPermissionOptions"
                    @change="onChangePermission"
                />
            </PaddedContent>
        </template>
    </HeaderCardsLayout>
</template>
<script setup lang="ts">
import auth from '@/auth/auth';
import AccentCard from '@/components/AccentCard/AccentCard.vue';
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue';
import BreadcrumbLink from '@/components/Breadcrumb/BreadcrumbLink.vue';
import { ButtonStatus } from '@/components/Button/Button.types';
import Button from '@/components/Button/Button.vue';
import LinkButton from '@/components/Button/LinkButton.vue';
import ContactInfo from '@/components/ContactInfo/ContactInfo.vue';
import CredentialsInfo from '@/components/CredentialsInfo/CredentialsInfo.vue';
import RadioButtonGroup from '@/components/Form/RadioButtonGroup.vue';
import AllDoneIcon from '@/components/Icon/AllDoneIcon.vue';
import BlockIcon from '@/components/Icon/BlockIcon.vue';
import DeleteForeverIcon from '@/components/Icon/DeleteForeverIcon.vue';
import UndoIcon from '@/components/Icon/UndoIcon.vue';
import UsersIcon from '@/components/Icon/UsersIcon.vue';
import WarningIcon from '@/components/Icon/WarningIcon.vue';
import HeaderCardsLayout from '@/components/Layout/HeaderCardsLayout.vue';
import PaddedContent from '@/components/Layout/PaddedContent.vue';
import LoginMethodHeader from '@/components/LoginMethodHeader/LoginMethodHeader.vue';
import { SsoProvider } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { computed, Ref, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useUser } from '../composables/useUser';

type LoginMethods = SsoProvider | 'password';

const props = defineProps<{ userId: string }>();

const userId = computed(() => props.userId);
const { user, loginMethodsWithActiveStatus, deactivateUserPasswordLogin, deactivateUserSsoLogin, updateUserIsAdmin } =
    useUser(userId);

const permissionChange = ref(user.value?.isAdmin ?? false);

const loginMethodsUpdate: Ref<Map<LoginMethods, boolean>> = ref(new Map(loginMethodsWithActiveStatus.value));

watch(
    user,
    (value) => {
        if (value) {
            permissionChange.value = value.isAdmin;
        }
    },
    { immediate: true },
);

watch(
    loginMethodsWithActiveStatus,
    (value) => {
        loginMethodsUpdate.value = new Map(value);
    },
    { immediate: true },
);

const router = useRouter();
const canEditPermissions = computed(() => auth.isAdmin());

const accessPermissionOptions = computed(() => [
    {
        id: 'read-and-write',
        label: $t('read-and-write'),
        isChecked: permissionChange.value === true,
        isDisabled: !canEditPermissions.value,
        value: 'read-and-write',
    },
    {
        id: 'read',
        label: $t('read'),
        isChecked: permissionChange.value === false,
        isDisabled: !canEditPermissions.value,
        value: 'read',
    },
    {
        id: 'no-read-or-write',
        label: $t('no-read-or-write'),
        isChecked: false,
        isDisabled: true,
        value: 'no-read-or-write',
        helpButtonText: $t('no-read-or-write-help'),
    },
]);

function removeLoginMethod(method: LoginMethods) {
    const next = new Map(loginMethodsUpdate.value);
    next.set(method, false);
    loginMethodsUpdate.value = next;
}

function keepLoginMethod(method: LoginMethods) {
    const next = new Map(loginMethodsUpdate.value);
    next.set(method, true);
    loginMethodsUpdate.value = next;
}

function loginMethodMarkedForRemoval(method: LoginMethods) {
    const currentValue = loginMethodsWithActiveStatus.value.get(method);
    const updateValue = loginMethodsUpdate.value.get(method);
    if (currentValue !== updateValue) {
        if (updateValue === false) {
            return true;
        }
        if (updateValue === true) {
            return false;
        }
    }
    return false;
}

function onChangePermission(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (value === 'read-and-write') {
        permissionChange.value = true;
    } else {
        permissionChange.value = false;
    }
}

function getAccentCardVariant(method: LoginMethods) {
    const updateValue = loginMethodsUpdate.value.get(method);
    const currentValue = loginMethodsWithActiveStatus.value.get(method);
    if (currentValue === true && updateValue === false) {
        return 'Gray';
    }
    if (method === 'sso_km') {
        return 'Koelnmesse';
    }
    return 'Yellow';
}

function getContactInfoVariant(method: LoginMethods) {
    if (loginMethodMarkedForRemoval(method)) {
        return 'Disabled';
    }
    return 'Default';
}

function loginMethodsHaveChanged(original: Map<LoginMethods, boolean>, updated: Map<LoginMethods, boolean>): boolean {
    if (original.size !== updated.size) return true;
    for (const [key, value] of original) {
        if (updated.get(key) !== value) return true;
    }
    return false;
}

const hasChanges = computed(() => {
    return (
        permissionChange.value !== user.value?.isAdmin ||
        loginMethodsHaveChanged(loginMethodsWithActiveStatus.value, loginMethodsUpdate.value)
    );
});

const wouldRemoveAllLoginMethods = computed(() => {
    if (loginMethodsUpdate.value.size === 0) return false;
    return [...loginMethodsUpdate.value.values()].every((isActive) => isActive === false);
});

const saveButtonDisabledExplanation = computed(() => {
    if (!auth.isAdmin()) return $t('no-permission-to-edit');
    if (!hasChanges.value) {
        return $t('no-form-changes');
    }
    if (wouldRemoveAllLoginMethods.value) {
        return $t('cannot-remove-all-login-methods');
    }
    return undefined;
});

const discardButtonDisabledExplanation = computed(() => {
    if (!hasChanges.value) {
        return $t('no-form-changes');
    }
    return undefined;
});

function onDiscardChanges() {
    permissionChange.value = user.value?.isAdmin ?? false;
    loginMethodsUpdate.value = new Map(loginMethodsWithActiveStatus.value);
}

const saveButtonStatus: Ref<ButtonStatus | undefined> = ref(undefined);

async function onSave() {
    if (!user.value) return;

    saveButtonStatus.value = undefined;

    try {
        for (const [key, value] of loginMethodsUpdate.value) {
            if (key === 'password' && value === false) {
                await deactivateUserPasswordLogin();
            } else if (key !== 'password' && value === false) {
                await deactivateUserSsoLogin(key);
            }
        }
        if (permissionChange.value !== user.value?.isAdmin) {
            await updateUserIsAdmin(permissionChange.value);
        }
        router.push({ name: 'user-details', params: { userId: user.value.id } });
    } catch {
        saveButtonStatus.value = 'Error';
    }
}
</script>
